import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const MedicareNonCoverageForm = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(true);
  const [medicareHistory, setMedicareHistory] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");

  const positions = [
    "RN",
    "PT",
    "PTA",
    "OT",
    "OTA",
    "ST",
    "HHA",
    "LVN",
    "MSW",
    "Caregiver",
    "CNA",
    "Office Assistant",
    "ADMIN"
  ];

  const [formData, setFormData] = useState({
    employeeId: "",
    employeeName: "",
    // Page 1 - Notice of Medicare Non-Coverage
    patientName: "",
    patientNumber: "",
    effectiveDate: "",
    serviceType: "",
    // Page 2 - Other Appeal Rights
    additionalInformation: "",
    patientSignature: "",
    signatureDate: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      const data = await getUsers(true);
      setUsers(data);
      setLoading(false);
    };
    loadUsers();

    // Load Medicare history from localStorage
    const savedHistory = localStorage.getItem("medicareHistory");
    if (savedHistory) {
      setMedicareHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    let fieldName = name || id;

    // If position is selected
    if (id === "selectedPosition") {
      setSelectedPosition(value);
      setFormData(prev => ({
        ...prev,
        employeeId: "",
        employeeName: ""
      }));
      return;
    }

    // If employee is selected, auto-populate name
    if (id === "employeeId") {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        const fullName = `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
        setFormData(prev => ({
          ...prev,
          employeeId: value,
          employeeName: fullName
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value
    }));
  };

  const formatDateUS = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "";
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month} / ${day} / ${year}`;
    } catch {
      return "";
    }
  };

  const generatePrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Notice of Medicare Non-Coverage - ${data.patientName || "Patient"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 9pt;
            line-height: 1.4;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #2c3e50;
            min-height: 90px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 18px;
          }
          .header h1 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            text-transform: uppercase;
          }
          .header h2 {
            font-size: 12pt;
            font-weight: bold;
            margin: 5px 0;
            color: #2c3e50;
          }
          .info-section {
            margin-bottom: 12px;
          }
          .info-row {
            display: flex;
            margin-bottom: 8px;
            font-size: 9pt;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 150px;
          }
          .info-value {
            border-bottom: 1px solid #333;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          .section {
            margin-bottom: 15px;
          }
          .section-title {
            font-size: 11pt;
            font-weight: bold;
            margin-bottom: 8px;
            color: #2c3e50;
            text-transform: uppercase;
          }
          .bullet-list {
            margin-left: 20px;
            margin-bottom: 10px;
          }
          .bullet-item {
            margin-bottom: 6px;
            line-height: 1.5;
            font-size: 9pt;
          }
          .divider {
            border-top: 1px solid #333;
            margin: 15px 0;
          }
          .signature-section {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #333;
          }
          .signature-row {
            display: flex;
            gap: 20px;
            margin-bottom: 10px;
            font-size: 9pt;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
          }
          .signature-value {
            border-bottom: 1px solid #333;
            min-height: 20px;
            padding-bottom: 3px;
            color: #212529;
          }
          .contact-info {
            margin-top: 10px;
            padding: 8px;
            background-color: #f8f9fa;
            border: 1px solid #ddd;
          }
          .contact-row {
            margin-bottom: 4px;
            font-size: 9pt;
          }
          .contact-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 120px;
          }
          .page-break {
            page-break-after: always;
          }
          .form-footer {
            margin-top: 20px;
            font-size: 7pt;
            color: #666;
            display: flex;
            justify-content: space-between;
            padding-top: 10px;
            border-top: 1px solid #ddd;
          }
        </style>
      </head>
      <body>
        <!-- PAGE 1 -->
        <div class="page-break">
          <div class="header">
            <div class="logo-container">
              <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
            </div>
            <div class="header-content">
              <h1>Infinite Home Health</h1>
              <h2>Notice of Medicare Non-Coverage</h2>
            </div>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Patient name:</span>
              <span class="info-value">${data.patientName || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Patient number:</span>
              <span class="info-value">${data.patientNumber || "________________"}</span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">The Effective Date Coverage of Your Current Home Health Services Will End:</div>
            <div class="info-row" style="margin-top: 8px;">
              <span class="info-value" style="min-width: 200px; font-weight: bold;">${formatDateShort(data.effectiveDate) || "________________"}</span>
            </div>
            <div class="bullet-list">
              <div class="bullet-item">• Your Medicare provider and/or health plan have determined that Medicare probably will not pay for your current ${data.serviceType || "{insert type}"} services after the effective date indicated above.</div>
              <div class="bullet-item">• You may have to pay for any services you receive after the above date.</div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="section">
            <div class="section-title">Your Right to Appeal This Decision:</div>
            <div class="bullet-list">
              <div class="bullet-item">• You have the right to an immediate, independent medical review (appeal) of the decision to end Medicare coverage of these services. Your services will continue during the appeal.</div>
              <div class="bullet-item">• If you choose to appeal, the independent reviewer will ask for your opinion. The reviewer also will look at your medical records and/or other relevant information. You do not have to prepare anything in writing, but you have the right to do so if you wish.</div>
              <div class="bullet-item">• If you choose to appeal, you and the independent reviewer will each receive a copy of the detailed explanation about why your coverage for services should not continue. You will receive this detailed notice only after you request an appeal.</div>
              <div class="bullet-item">• If you choose to appeal, and the independent reviewer agrees services should no longer be covered after the effective date indicated above;
                <div style="margin-left: 20px; margin-top: 4px;">- Neither Medicare nor your plan will pay for these services after that date.</div>
              </div>
              <div class="bullet-item">• If you stop services no later than the effective date indicated above, you will avoid financial liability.</div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="section">
            <div class="section-title">How to Ask For an Immediate Appeal:</div>
            <div class="bullet-list">
              <div class="bullet-item">• You must make your request to your Quality Improvement Organization (also known as a QIO). A QIO is the independent reviewer authorized by Medicare to review the decision to end these services.</div>
              <div class="bullet-item">• Your request for an immediate appeal should be made as soon as possible, but no later than noon of the day before the effective date indicated above.</div>
              <div class="bullet-item">• The QIO will notify you of its decision as soon as possible, generally no later than two days after the effective date of this notice if you are in Original Medicare. If you are in a Medicare health plan, the QIO generally will notify you of its decision by the effective date of this notice.</div>
              <div class="bullet-item">• Call your QIO at: Health Services Advisory Group, Inc (HSAG) at 1-800-841-1602</div>
              <div class="bullet-item" style="margin-left: 40px;">• See page 2 of this notice for more information.</div>
            </div>
          </div>

          <div class="form-footer">
            <span>Form CMS 10123-NOMNC (Approved 12/31/2011)</span>
            <span>OMB approval 0938-0963</span>
          </div>
        </div>

        <!-- PAGE 2 -->
        <div>
          <div class="header">
            <h2>If You Miss The Deadline to Request An Immediate Appeal, You May Have Other Appeal Rights:</h2>
          </div>

          <div class="section">
            <div class="bullet-list">
              <div class="bullet-item">• If you have Original Medicare: Call the QIO listed on page 1.</div>
              <div class="bullet-item">• If you belong to a Medicare health plan: Call your plan at the number given below.</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Plan contact information:</div>
            <div class="contact-info">
              <div class="contact-row">
                <span class="contact-label">Organization Name:</span>
                <span>Kaiser Foundation Health Plan, Inc</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Attention:</span>
                <span>Attn.: Expedited Appeals</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Toll Free Telephone:</span>
                <span>1-888-987-7247</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">TTY Number:</span>
                <span>1-800-777-1370</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Toll Free Fax:</span>
                <span>1-888-987-2252</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Additional Information (Optional):</div>
            <div style="min-height: 80px; border: 1px solid #ddd; padding: 8px; margin-top: 8px;">
              ${data.additionalInformation || ""}
            </div>
          </div>

          <div class="signature-section">
            <div style="margin-bottom: 10px; font-size: 9pt;">
              Please sign below to indicate you received and understood this notice.
            </div>
            <div style="margin-bottom: 10px; font-size: 9pt; line-height: 1.5;">
              I have been notified that coverage of my services will end on the effective date indicated on this notice and that I may appeal this decision by contacting my QIO.
            </div>
            <div class="signature-row">
              <div class="signature-field">
                <div class="signature-label">Signature of Patient or Representative:</div>
                <div class="signature-value">${data.patientSignature || "________________________________________________________________________________"}</div>
              </div>
            </div>
            <div class="signature-row">
              <div class="signature-field">
                <div class="signature-label">Date:</div>
                <div class="signature-value">${formatDateUS(data.signatureDate) || "________________"}</div>
              </div>
            </div>
          </div>

          <div style="margin-top: 20px; font-size: 7pt; color: #666; display: flex; justify-content: space-between;">
            <span>Form CMS 10123-NOMNC (Approved 12/31/2011)</span>
            <span>OMB approval 0938-0963</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printForm = (data) => {
    const printHTML = generatePrintFormHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };

    // Save to Medicare history
    const historyEntry = {
      id: Date.now(),
      employeeName: formData.employeeName,
      employeeId: formData.employeeId,
      patientName: formData.patientName,
      submittedAt: new Date().toISOString(),
      data: submitData
    };

    const updatedHistory = [historyEntry, ...medicareHistory];
    setMedicareHistory(updatedHistory);
    localStorage.setItem('medicareHistory', JSON.stringify(updatedHistory));

    // Automatically trigger print, then reset form
    setTimeout(() => {
      printForm(submitData);
      resetForm();
      setSelectedPosition("");
      setShowForm(false);
      setShowUserList(true);
      setCurrentPage(1);
    }, 300);
  };

  const resetForm = () => {
    setFormData({
      employeeId: "",
      employeeName: "",
      patientName: "",
      patientNumber: "",
      effectiveDate: "",
      serviceType: "",
      additionalInformation: "",
      patientSignature: "",
      signatureDate: "",
    });
    setSelectedPosition("");
    setCurrentPage(1);
  };

  const nextPage = () => {
    if (currentPage < 2) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Medicare Non-Coverage Form</h1>
        <button
          onClick={() => navigate("/forms")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          Back to Forms
        </button>
      </div>

      {showUserList && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Employee</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position *
            </label>
            <select
              id="selectedPosition"
              value={selectedPosition}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select a position...</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name *
            </label>
            <input
              type="text"
              name="employeeName"
              value={formData.employeeName || ""}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter employee name"
              required
            />
          </div>
          <button
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!formData.employeeName || formData.employeeName.trim() === "") {
                alert("Please enter employee name");
                return;
              }
              setShowUserList(false);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            disabled={!selectedPosition || !formData.employeeName || formData.employeeName.trim() === ""}
          >
            Continue
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Information Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="employeeName"
                    value={formData.employeeName || ""}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter employee name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Page 1 */}
            {currentPage === 1 && (
              <div className="space-y-6">
                <div className="border-b border-gray-300 pb-2">
                  <h3 className="text-lg font-semibold text-gray-800">Page 1 of 2 - Notice of Medicare Non-Coverage</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient name: *
                    </label>
                    <input
                      type="text"
                      name="patientName"
                      value={formData.patientName}
                      onChange={handleInputChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient number: *
                    </label>
                    <input
                      type="text"
                      name="patientNumber"
                      value={formData.patientNumber}
                      onChange={handleInputChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                {/* Effective Date Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    The Effective Date Coverage of Your Current Home Health Services Will End:
                  </h4>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Effective Date: *
                    </label>
                    <DateInput
                      name="effectiveDate"
                      value={formData.effectiveDate}
                      onChange={handleInputChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Type: *
                    </label>
                    <input
                      type="text"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      placeholder="e.g., home health services"
                      required
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-gray-700">
                    <p>• Your Medicare provider and/or health plan have determined that Medicare probably will not pay for your current <strong>{formData.serviceType || "{insert type}"}</strong> services after the effective date indicated above.</p>
                    <p>• You may have to pay for any services you receive after the above date.</p>
                  </div>
                </div>

                {/* Your Right to Appeal Section */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    Your Right to Appeal This Decision:
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>• You have the right to an immediate, independent medical review (appeal) of the decision to end Medicare coverage of these services. Your services will continue during the appeal.</p>
                    <p>• If you choose to appeal, the independent reviewer will ask for your opinion. The reviewer also will look at your medical records and/or other relevant information. You do not have to prepare anything in writing, but you have the right to do so if you wish.</p>
                    <p>• If you choose to appeal, you and the independent reviewer will each receive a copy of the detailed explanation about why your coverage for services should not continue. You will receive this detailed notice only after you request an appeal.</p>
                    <p>• If you choose to appeal, and the independent reviewer agrees services should no longer be covered after the effective date indicated above;
                      <span className="block ml-4 mt-1">- Neither Medicare nor your plan will pay for these services after that date.</span>
                    </p>
                    <p>• If you stop services no later than the effective date indicated above, you will avoid financial liability.</p>
                  </div>
                </div>

                {/* How to Ask For an Immediate Appeal Section */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    How to Ask For an Immediate Appeal:
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>• You must make your request to your Quality Improvement Organization (also known as a QIO). A QIO is the independent reviewer authorized by Medicare to review the decision to end these services.</p>
                    <p>• Your request for an immediate appeal should be made as soon as possible, but no later than noon of the day before the effective date indicated above.</p>
                    <p>• The QIO will notify you of its decision as soon as possible, generally no later than two days after the effective date of this notice if you are in Original Medicare. If you are in a Medicare health plan, the QIO generally will notify you of its decision by the effective date of this notice.</p>
                    <p className="font-semibold">• Call your QIO at: Health Services Advisory Group, Inc (HSAG) at <span className="text-blue-600">1-800-841-1602</span></p>
                    <p className="text-xs text-gray-600 italic">• See page 2 of this notice for more information.</p>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t border-gray-300">
                  <button
                    type="button"
                    onClick={nextPage}
                    className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Next Page
                  </button>
                </div>
              </div>
            )}

            {/* Page 2 */}
            {currentPage === 2 && (
              <div className="space-y-6">
                <div className="border-b border-gray-300 pb-2">
                  <h3 className="text-lg font-semibold text-gray-800">Page 2 of 2 - Other Appeal Rights</h3>
                </div>

                {/* Other Appeal Rights Section */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    If You Miss The Deadline to Request An Immediate Appeal, You May Have Other Appeal Rights:
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>• If you have Original Medicare: Call the QIO listed on page 1.</p>
                    <p>• If you belong to a Medicare health plan: Call your plan at the number given below.</p>
                  </div>
                </div>

                {/* Plan Contact Information Section */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    Plan contact information:
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="font-semibold">Organization Name:</span>
                        <p>Kaiser Foundation Health Plan, Inc</p>
                      </div>
                      <div>
                        <span className="font-semibold">Attention:</span>
                        <p>Attn.: Expedited Appeals</p>
                      </div>
                      <div>
                        <span className="font-semibold">Toll Free Telephone:</span>
                        <p className="text-blue-600">1-888-987-7247</p>
                      </div>
                      <div>
                        <span className="font-semibold">TTY Number:</span>
                        <p className="text-blue-600">1-800-777-1370</p>
                      </div>
                      <div>
                        <span className="font-semibold">Toll Free Fax:</span>
                        <p className="text-blue-600">1-888-987-2252</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    Additional Information (Optional):
                  </h4>
                  <textarea
                    name="additionalInformation"
                    value={formData.additionalInformation}
                    onChange={handleInputChange}
                    rows="6"
                    placeholder="Enter any additional information here..."
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                {/* Acknowledgment and Signature Section */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">
                    Acknowledgment and Signature
                  </h4>
                  <div className="space-y-3 text-sm text-gray-700 mb-4">
                    <p className="font-medium">
                      Please sign below to indicate you received and understood this notice.
                    </p>
                    <p>
                      I have been notified that coverage of my services will end on the effective date indicated on this notice and that I may appeal this decision by contacting my QIO.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signature of Patient or Representative: *
                      </label>
                      <input
                        type="text"
                        name="patientSignature"
                        value={formData.patientSignature}
                        onChange={handleInputChange}
                        required
                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date: *
                      </label>
                      <DateInput
                        name="signatureDate"
                        value={formData.signatureDate}
                        onChange={handleInputChange}
                        required
                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-4 pt-4 border-t border-gray-300">
                  <button
                    type="button"
                    onClick={prevPage}
                    className="bg-gray-300 text-gray-700 py-2 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                  >
                    Previous Page
                  </button>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setShowUserList(true);
                        resetForm();
                        setSelectedPosition("");
                      }}
                      className="bg-gray-300 text-gray-700 py-2 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
                    >
                      Submit/Print
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default MedicareNonCoverageForm;

